import {
    createRouter,
    createWebHistory,
    NavigationGuardNext,
    RouteLocationNormalized,
    RouteRecordRaw,
} from 'vue-router'

import authMiddleware from '@/middlewares/auth.middleware'
import guestMiddleware from '@/middlewares/guest.middleware'
import MainPage from '@/views/common/MainPage.vue'
import NotFoundPage from '@/views/common/NotFoundPage.vue'
import RedirectingPage from '@/views/common/RedirectingPage.vue'
import { carryClient, recordClient } from '@/views/inventory/materials-management/materialsClient'
import { formerUsersPathTarget, legacyUserManagementTarget } from '@/views/admin/users/usersRoutes'
import SignInPage from '@/views/common/SignInPage.vue'

const routes: Array<RouteRecordRaw> = [
    {
        path: '/:pathMatch(.*)*',
        name: 'page-not-found',
        component: NotFoundPage,
        meta: { title: 'Channel Optimiser | 404 Page Not Found' },
    },
    {
        path: '/',
        name: 'home',
        component: MainPage,
        meta: { middleware: [authMiddleware], title: 'Channel Optimiser | Main' },
    },
    {
        path: '/redirecting',
        name: 'redirecting',
        component: RedirectingPage,
        meta: { title: 'Channel Optimiser | Redirecting' },
    },
    {
        path: '/sign-in',
        name: 'sign.in',
        component: SignInPage,
        meta: {
            middleware: [guestMiddleware],
            title: 'Channel Optimiser | Welcome',
        },
    },
    {
        path: '/order/sales-orders',
        name: 'order.sales.orders',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Sales Orders',
        },
        component: () => import('@/views/order/sales-orders/SalesOrdersPage.vue'),
    },
    {
        path: '/order/sales-orders/:id',
        name: 'order.sales-orders.view',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Order',
        },
        component: () => import('@/views/order/sales-orders/order/SalesOrderPage.vue'),
    },
    {
        path: '/order/order-shipments',
        name: 'order.order-shipments',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Order Shipments',
        },
        component: () => import('@/views/order/order-shipments/OrderShipmentsSearchPage.vue'),
    },
    {
        path: '/order/order-shipments/:id',
        name: 'order.order-shipments.view',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Order Shipment',
        },
        component: () => import('@/views/order/order-shipments/OrderShipmentDetailPage.vue'),
    },
    {
        path: '/order/live-order-tracker',
        name: 'order.live.order.tracker',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Live Order Tracker',
        },
        component: () => import('@/views/order/live-order-tracker/LiveOrderTrackerPage.vue'),
    },
    {
        path: '/channel/clients',
        name: 'channel.clients',
        component: () => import('@/views/channel/clients/ClientsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Clients',
        },
    },
    {
        path: '/customer-service/rma',
        name: 'customer-service.rma',
        component: () => import('@/views/customer-service/rma/RmaSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | PSOQ Search',
        },
    },
    {
        path: '/customer-service/rma/create',
        name: 'customer-service.rma.create',
        component: () => import('@/views/customer-service/rma/RmaCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | PSOQ Create',
        },
    },
    {
        path: '/customer-service/rma/:psoqNumber',
        name: 'customer-service.rma.view',
        component: () => import('@/views/customer-service/rma/RmaPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | PSOQ',
        },
    },
    {
        path: '/order/failed-imports',
        name: 'order.failed-imports',
        component: () => import('@/views/order/failed-imports/FailedImportsSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Failed Imports',
        },
    },
    {
        path: '/order/failed-imports/:id',
        name: 'order.failed-imports.view',
        component: () => import('@/views/order/failed-imports/FailedImportPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Failed Import',
        },
    },
    {
        path: '/order/purchase-orders',
        name: 'order.purchase-orders',
        component: () => import('@/views/order/purchase-orders/PurchaseOrdersSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Purchase Orders',
        },
    },
    {
        path: '/order/purchase-orders/new',
        name: 'order.purchase-orders.create',
        component: () => import('@/views/order/purchase-orders/PurchaseOrderFormPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Create Purchase Order',
        },
    },
    {
        path: '/order/purchase-orders/:id/edit',
        name: 'order.purchase-orders.edit',
        component: () => import('@/views/order/purchase-orders/PurchaseOrderFormPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Edit Purchase Order',
        },
    },
    {
        path: '/order/purchase-orders/:id',
        name: 'order.purchase-orders.view',
        component: () => import('@/views/order/purchase-orders/PurchaseOrderPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Purchase Order',
        },
    },
    {
        path: '/order/purchase-orders/:id/deliveries',
        name: 'order.purchase-orders.deliveries',
        component: () => import('@/views/order/purchase-orders/PurchaseOrderDeliveriesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Purchase Order Deliveries',
        },
    },
    {
        path: '/analysis/amazon-repricer',
        name: 'analysis.amazon.repricer',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Amazon Repricer',
        },
        component: () => import('@/views/analysis/amazon-repricer/AmazonRepricerPage.vue'),
        children: [
            {
                path: '/analysis/amazon-repricer/logs',
                name: 'analysis.amazon.repricer.logs',
                component: () => import('@/views/analysis/amazon-repricer/repricer-logs/RepricerLogsPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer-sales-channels/logs',
                name: 'analysis.amazon.repricer.sales.channels.logs',
                component: () => import('@/views/analysis/amazon-repricer/repricer-logs/RepricerLogsPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/listings',
                name: 'analysis.amazon.repricer.listings',
                component: () => import('@/views/analysis/amazon-repricer/repricer-listings/RepricerListingsPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/sales-channels-listings',
                name: 'analysis.amazon.repricer.sales.channels.listings',
                component: () => import('@/views/analysis/amazon-repricer/repricer-listings/RepricerListingsPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/logs/previous-updates',
                name: 'analysis.amazon.repricer.logs.previous-updates',
                component: () =>
                    import('@/views/analysis/amazon-repricer/repricer-logs/RepricerPreviousUpdatesPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer-sales-channels/logs/previous-updates',
                name: 'analysis.amazon.repricer.sales.channels.logs.previous-updates',
                component: () =>
                    import('@/views/analysis/amazon-repricer/repricer-logs/RepricerPreviousUpdatesPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/logs/review-updates',
                name: 'analysis.amazon.repricer.logs.review-updates',
                component: () => import('@/views/analysis/amazon-repricer/repricer-logs/RepricerReviewUpdatesPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer-sales-channels/logs/review-updates',
                name: 'analysis.amazon.repricer.sales.channels.logs.review-updates',
                component: () => import('@/views/analysis/amazon-repricer/repricer-logs/RepricerReviewUpdatesPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/logs/:id',
                name: 'analysis.amazon.repricer.log',
                component: () => import('@/views/analysis/amazon-repricer/repricer-log/RepricerLogPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/listings/:logId',
                name: 'analysis.amazon.repricer.listing',
                component: () => import('@/views/analysis/amazon-repricer/repricer-listing/RepricerListingPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/health',
                name: 'analysis.amazon.repricer.health',
                component: () => import('@/views/analysis/amazon-repricer/repricer-health/RepricerHealthPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/sales-channels-logs/:id',
                name: 'analysis.amazon.repricer.sales.channels.logs.view',
                component: () => import('@/views/analysis/amazon-repricer/repricer-log/RepricerLogPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/sisense',
                name: 'analysis.amazon.repricer.sisense',
                component: () => import('@/views/analysis/amazon-repricer/sisense/AmazonRepricerSisensePage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/units',
                name: 'analysis.amazon.repricer.units',
                component: () => import('@/views/analysis/amazon-repricer/repricer-units/AmazonRepricerUnitsPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/units/:id',
                name: 'analysis.amazon.repricer.units.edit',
                component: () => import('@/views/analysis/amazon-repricer/repricer-units/AmazonRepricerUnitPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/units/create',
                name: 'analysis.amazon.repricer.units.create',
                component: () => import('@/views/analysis/amazon-repricer/repricer-units/AmazonRepricerUnitPage.vue'),
            },
            {
                path: '/analysis/amazon-repricer/units/:id/affected-asins',
                name: 'analysis.amazon.repricer.units.affected-asins',
                component: () =>
                    import(
                        '@/views/analysis/amazon-repricer/repricer-units/affected-asins/AmazonRepricerUnitAffectedAsinsPage.vue'
                    ),
            },
        ],
    },
    {
        path: '/analysis/invoices-validation',
        name: 'analysis.invoices.validation',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Invoices Validation',
        },
        component: () => import('@/views/analysis/invoices-validation/InvoiceValidationPage.vue'),
    },
    {
        path: '/analysis/asins-analysis',
        name: 'analysis.asins.analysis',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | ASINs Analysis',
        },
        component: () => import('@/views/analysis/asin-analysis/AsinsAnalysisPage.vue'),
    },
    {
        path: '/analysis/asins-analysis/:id',
        name: 'analysis.asin.analysis',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | ASIN Analysis',
        },
        component: () => import('@/views/analysis/asin-analysis/AsinAnalysisPage.vue'),
    },
    {
        path: '/analysis/sp-collector',
        name: 'analysis.sp.collector',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | SP Collector',
        },
        component: () => import('@/views/analysis/sp-collector/SPCollectiorPage.vue'),
    },
    {
        path: '/analysis/sp-collector/listings/:id',
        name: 'analysis.sp.collector.listing',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | SP Collector Listing',
        },
        component: () => import('@/views/analysis/sp-collector/SPCollectorListingDetailPage.vue'),
    },
    {
        path: '/inventory/tax-classes',
        name: 'inventory.tax-classes',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Tax Classes',
        },
        component: () => import('@/views/inventory/tax-classes/TaxClassesPage.vue'),
    },
    {
        path: '/inventory/materials-management',
        name: 'inventory.materials-management',
        redirect: '/inventory/materials-management/overview',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Materials Management',
        },
        component: () => import('@/views/inventory/materials-management/MaterialsManagementPage.vue'),
        children: [
            {
                path: '/inventory/materials-management/overview',
                name: 'inventory.materials-management.overview',
                component: () =>
                    import('@/views/inventory/materials-management/overview/MaterialsManagementOverviewPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Overview',
                    materialsClient: 'many',
                },
            },
            {
                path: '/inventory/materials-management/explorer',
                name: 'inventory.materials-management.explorer',
                component: () =>
                    import('@/views/inventory/materials-management/explorer/MaterialsManagementExplorerPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Explorer',
                    materialsClient: 'many',
                },
            },
            {
                path: '/inventory/materials-management/materials/:id(\\d+)',
                name: 'inventory.materials-management.material',
                component: () =>
                    import('@/views/inventory/materials-management/material/MaterialsManagementMaterialPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Material',
                },
            },
            {
                path: '/inventory/materials-management/bundles/:id(\\d+)',
                name: 'inventory.materials-management.bundle',
                component: () =>
                    import('@/views/inventory/materials-management/bundle/MaterialsManagementBundlePage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Bundle',
                },
            },
            {
                path: '/inventory/materials-management/mappings',
                name: 'inventory.materials-management.mappings',
                component: () =>
                    import('@/views/inventory/materials-management/mappings/MaterialsManagementMappingsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Mappings',
                    materialsClient: 'many',
                },
            },
            {
                path: '/inventory/materials-management/profiles/:id(\\d+)',
                name: 'inventory.materials-management.profile',
                component: () =>
                    import('@/views/inventory/materials-management/profile/MaterialsManagementProfilePage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Channel Profile',
                },
            },
            {
                path: '/inventory/materials-management/imports',
                name: 'inventory.materials-management.imports',
                component: () =>
                    import('@/views/inventory/materials-management/imports/MaterialsManagementImportsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Materials Management Imports',
                    materialsClient: 'one',
                },
            },
        ],
    },
    {
        path: '/report/business-intelligence',
        name: 'report.business-intelligence',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Business Intelligence',
        },
        component: () => import('@/views/report/business-intelligence/BusinessIntelligencePage.vue'),
    },
    {
        path: '/report/finance-report',
        name: 'report.finance-report',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Finance Report',
        },
        component: () => import('@/views/report/finance-report/FinanceReportPage.vue'),
    },
    {
        path: '/inventory/inventory-bundles',
        name: 'inventory.inventory.bundles',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Inventory Bundles',
        },
        component: () => import('@/views/inventory/inventory-bundles/InventoryBundlesPage.vue'),
    },
    {
        path: '/inventory/bundle-recommendations',
        name: 'inventory.bundle.recommendations',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Bundle Recommendations',
        },
        component: () => import('@/views/inventory/bundle-recommendations/BundleRecommendationsPage.vue'),
    },
    {
        path: '/inventory/bundle-recommendations/custom',
        name: 'inventory.bundle.recommendations.prepare.custom',
        component: () => import('@/views/inventory/bundle-recommendations/PrepareBundlePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Prepare Custom Bundle',
        },
    },
    {
        path: '/inventory/bundle-recommendations/:id(\\d+)',
        name: 'inventory.bundle.recommendations.prepare.bundle',
        component: () => import('@/views/inventory/bundle-recommendations/PrepareBundlePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Prepare Bundle',
        },
    },
    {
        path: '/inventory/inventory-bundles/:id',
        name: 'inventory.inventory.bundle',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Inventory Bundle',
        },
        component: () => import('@/views/inventory/inventory-bundles/InventoryBundlePage.vue'),
    },
    {
        path: '/inventory/portfolio-management',
        name: 'inventory.portfolio.management',
        redirect: '/inventory/portfolio-management/portfolio',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Portfolio Management',
        },
        component: () => import('@/views/inventory/portfolio-management/PortfolioManagementPage.vue'),
        children: [
            {
                path: '/inventory/portfolio-management/portfolio',
                name: 'inventory.portfolio.management.portfolio',
                component: () =>
                    import('@/views/inventory/portfolio-management/portfolio/PortfolioManagementPortfolioPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Portfolio List',
                },
            },
            {
                path: '/inventory/portfolio-management/portfolio/:id',
                name: 'inventory.portfolio.management.portfolio.single',
                component: () =>
                    import('@/views/inventory/portfolio-management/PortfolioManagementPortfolioSinglePage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Listing Detailed Overview',
                },
            },
            {
                path: '/inventory/portfolio-management/market-explorer',
                name: 'inventory.portfolio.management.market.explorer',
                component: () =>
                    import(
                        '@/views/inventory/portfolio-management/market-explorer/PortfolioManagementMarketExplorerPage.vue'
                    ),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Market Explorer',
                },
            },
            {
                path: '/inventory/portfolio-management/content-center',
                name: 'inventory.portfolio.management.content.center',
                component: () =>
                    import(
                        '@/views/inventory/portfolio-management/content-center/PortfolioManagementContentCenterPage.vue'
                    ),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Content Center',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower',
                name: 'inventory.portfolio.management.act',
                component: () => import('@/views/inventory/portfolio-management/act/PortfolioManagementACTPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Amazon Control Tower',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower/image-optimisation',
                name: 'inventory.portfolio.management.act.imageOptimisation',
                component: () =>
                    import('@/views/inventory/portfolio-management/act/impactful-to-dos/ImageOptimisationPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Image Optimisation',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower/buy-box-opportunities',
                name: 'inventory.portfolio.management.act.buyBoxOpportunities',
                component: () =>
                    import('@/views/inventory/portfolio-management/act/impactful-to-dos/BuyBoxOpportunitiesPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Buy Box Opportunities',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower/rival-sellers',
                name: 'inventory.portfolio.management.act.rivalSellers',
                component: () =>
                    import('@/views/inventory/portfolio-management/act/impactful-to-dos/RivalSellersPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Rival Sellers',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower/suppressed-buy-boxes',
                name: 'inventory.portfolio.management.act.suppressedBuyBoxes',
                component: () =>
                    import('@/views/inventory/portfolio-management/act/impactful-to-dos/SuppressedBuyBoxesPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Suppressed Buy Boxes',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower/margin-optimization',
                name: 'inventory.portfolio.management.act.marginOptimization',
                component: () =>
                    import('@/views/inventory/portfolio-management/act/impactful-to-dos/MarginOptimizationPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Margin Optimization',
                },
            },
            {
                path: '/inventory/portfolio-management/amazon-control-tower/amazon-out-of-stock',
                name: 'inventory.portfolio.management.act.amazonOutOfStock',
                component: () =>
                    import('@/views/inventory/portfolio-management/act/impactful-to-dos/AmazonOutOfStockPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Amazon Out of Stock',
                },
            },
            {
                path: '/inventory/portfolio-management/groups',
                name: 'inventory.portfolio.management.groups',
                component: () =>
                    import('@/views/inventory/portfolio-management/groups/PortfolioManagementGroupsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Groups',
                },
            },
            {
                path: '/inventory/portfolio-management/groups/:id',
                name: 'inventory.portfolio.management.group.edit',
                component: () =>
                    import('@/views/inventory/portfolio-management/groups/PortfolioManagementGroupPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Group',
                },
            },
            {
                path: '/inventory/portfolio-management/groups/create',
                name: 'inventory.portfolio.management.group.create',
                component: () =>
                    import('@/views/inventory/portfolio-management/groups/PortfolioManagementGroupPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Group',
                },
            },
        ],
    },
    {
        path: '/channel/channel-profiles',
        name: 'channel.channel-profiles',
        component: () => import('@/views/channel/channel-profiles/ChannelProfilesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Channel Profiles',
        },
    },
    {
        path: '/channel/channel-profiles/create',
        name: 'channel.channel-profiles.create',
        component: () => import('@/views/channel/channel-profiles/ChannelProfileFormPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Channel Profile',
        },
    },
    {
        path: '/channel/channel-profiles/:id/edit',
        name: 'channel.channel-profiles.edit',
        component: () => import('@/views/channel/channel-profiles/ChannelProfileFormPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Channel Profile',
        },
    },
    {
        path: '/customer-service/customer-search',
        name: 'customer-service.customer-search',
        component: () => import('@/views/customer-service/customer-search/CustomerSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Customer Search',
        },
    },
    {
        path: '/customer-service/customer-search/:id',
        name: 'customer-service.customer-search.view',
        component: () => import('@/views/customer-service/customer-search/CustomerDetailPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Customer',
        },
    },
    {
        path: '/customer-service/credit-notes',
        name: 'customer-service.credit-notes',
        component: () => import('@/views/customer-service/credit-notes/CreditNotesSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Credit Notes',
        },
    },
    {
        path: '/customer-service/credit-notes/create',
        name: 'customer-service.credit-notes.create',
        component: () => import('@/views/customer-service/credit-notes/CreditNoteCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Create Credit Note',
        },
    },
    {
        path: '/customer-service/credit-notes/:id/edit',
        name: 'customer-service.credit-notes.edit',
        component: () => import('@/views/customer-service/credit-notes/CreditNoteCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Edit Credit Note',
        },
    },
    {
        path: '/customer-service/credit-notes/:id',
        name: 'customer-service.credit-notes.view',
        component: () => import('@/views/customer-service/credit-notes/CreditNotePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Credit Note',
        },
    },
    {
        path: '/support',
        name: 'support',
        component: () => import('@/views/support/SupportPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Support',
        },
    },
    {
        path: '/support/release-notes',
        name: 'support.release-notes',
        component: () => import('@/views/support/ReleaseNotesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Release Notes',
        },
    },
    {
        path: '/admin/email-templates',
        name: 'admin.email-templates',
        component: () => import('@/views/admin/email-templates/EmailTemplatesSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Email Templates',
        },
    },
    {
        path: '/admin/email-templates/create',
        name: 'admin.email-templates.create',
        component: () => import('@/views/admin/email-templates/EmailTemplateCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Create Email Template',
        },
    },
    {
        path: '/admin/email-templates/:id/edit',
        name: 'admin.email-templates.edit',
        component: () => import('@/views/admin/email-templates/EmailTemplateCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Edit Email Template',
        },
    },
    {
        path: '/admin/layouts',
        name: 'admin.layouts',
        component: () => import('@/views/admin/layouts/LayoutsSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Layouts',
        },
    },
    {
        path: '/admin/layouts/create',
        name: 'admin.layouts.create',
        component: () => import('@/views/admin/layouts/LayoutEditPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Create Layout',
        },
    },
    {
        path: '/admin/layouts/:id/edit',
        name: 'admin.layouts.edit',
        component: () => import('@/views/admin/layouts/LayoutEditPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Layout',
        },
    },
    {
        path: '/admin/product-road-map',
        name: 'admin.product-road-map',
        component: () => import('@/views/admin/product-road-map/ProductRoadMapPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Product Road Map',
        },
    },
    {
        path: '/channel/forsure-clients',
        name: 'channel.forsure-clients',
        component: () => import('@/views/channel/forsure-clients/ForSureClientsSearchPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | ForSure Clients',
        },
    },
    {
        path: '/channel/forsure-clients/create',
        name: 'channel.forsure-clients.create',
        component: () => import('@/views/channel/forsure-clients/ForSureClientCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Create ForSure Configuration',
        },
    },
    {
        path: '/channel/forsure-clients/:id/edit',
        name: 'channel.forsure-clients.edit',
        component: () => import('@/views/channel/forsure-clients/ForSureClientCreatePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Edit ForSure Configuration',
        },
    },
    {
        path: '/admin/job-history-logs',
        name: 'admin.job-history-logs',
        component: () => import('@/views/admin/job-history-logs/JobHistoryLogsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Job History Logs',
        },
    },
    {
        path: '/admin/job-history-logs/:id',
        name: 'admin.job-history-logs.view',
        component: () => import('@/views/admin/job-history-logs/JobHistoryLogPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Job History Log',
        },
    },
    {
        path: '/admin/release-manager',
        name: 'admin.release-manager',
        component: () => import('@/views/admin/release-manager/ReleaseManagerPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Release Manager',
        },
    },
    {
        path: '/admin/release-manager/history',
        name: 'admin.release-manager-history',
        component: () => import('@/views/admin/release-manager/history/ReleaseManagerHistoryPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Release Manager History',
        },
    },
    {
        path: '/admin/system-events-monitor',
        name: 'admin.system-events-monitor',
        component: () => import('@/views/admin/system-events-monitor/SystemEventsMonitorPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | System Events Monitor',
        },
    },
    {
        path: '/admin/data-processing-scheduler-jobs',
        name: 'admin.data-processing-scheduler-jobs',
        component: () => import('@/views/admin/data-processing-scheduler-jobs/DataProcessingSchedulerJobsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Data Processing Scheduler Jobs',
        },
    },
    {
        path: '/admin/message-queues',
        name: 'admin.message-queues',
        component: () => import('@/views/admin/message-queues/MessageQueuesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Message Queues',
        },
    },
    {
        path: '/admin/braintree-transactions',
        name: 'admin.braintree-transactions',
        component: () => import('@/views/admin/braintree-transactions/BraintreeTransactionsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Braintree Transactions',
        },
    },
    {
        path: '/admin/supplier-messages',
        name: 'admin.supplier-messages',
        component: () => import('@/views/admin/supplier-messages/SupplierMessagesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Supplier Messages',
        },
    },
    {
        path: '/admin/supplier-messages/:id/logs',
        name: 'admin.supplier-messages.logs',
        component: () => import('@/views/admin/supplier-messages/SupplierMessageLogsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Message History Logs',
        },
    },
    {
        path: '/admin/supplier-messages/:id/edit',
        name: 'admin.supplier-messages.edit',
        component: () => import('@/views/admin/supplier-messages/SupplierMessageEditPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Edit Message Status',
        },
    },
    {
        path: '/admin/system-logs',
        name: 'admin.system.log',
        redirect: '/admin/system-logs/api-logs',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | System Logs',
        },
        component: () => import('@/views/admin/system-logs/SystemLogsPage.vue'),
        children: [
            {
                path: '/admin/system-logs/cloud-watch/log-groups',
                name: 'admin.system.log.cloud.watch.log.groups',
                component: () =>
                    import('@/views/admin/system-logs/cloud-watch-logs/log-groups/CloudWatchLogGroupsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | CloudWatch Log Groups',
                },
            },
            {
                path: '/admin/system-logs/cloud-watch/log-group-streams/:logGroupName',
                name: 'admin.system.log.cloud.watch.log.group.streams',
                component: () =>
                    import(
                        '@/views/admin/system-logs/cloud-watch-logs/log-group-streams/CloudWatchLogGroupStreams.vue'
                    ),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | CloudWatch Log Group Streams',
                },
            },
            {
                path: '/admin/system-logs/cloud-watch/log-group-stream-events/:logGroupName/log-events',
                name: 'admin.system.log.cloud.watch.log.group.log.events',
                component: () =>
                    import('@/views/admin/system-logs/cloud-watch-logs/log-events/CloudWatchLogEventsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | CloudWatch Log Group All Events',
                },
            },
            {
                path: '/admin/system-logs/cloud-watch/log-group-stream-events/:logGroupName/:logStreamName',
                name: 'admin.system.log.cloud.watch.log.group.stream.events',
                component: () =>
                    import(
                        '@/views/admin/system-logs/cloud-watch-logs/log-group-stream-events/CloudWatchLogGroupStreamEvents.vue'
                    ),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | CloudWatch Log Group Stream Events',
                },
            },
            {
                path: '/admin/system-logs/api-logs',
                name: 'admin.system.log.api.logs',
                component: () => import('@/views/admin/system-logs/api-logs/SystemLogsApiLogsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | API Logs',
                },
            },
            {
                path: '/admin/system-logs/lambda-logs',
                name: 'admin.system.log.lambda.logs',
                component: () => import('@/views/admin/system-logs/lambda-logs/LambdaLogsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Lambda Logs',
                },
            },
            {
                path: '/admin/system-logs/marketplace-feeds',
                name: 'admin.system.log.marketplace.feeds',
                component: () =>
                    import('@/views/admin/system-logs/marketplace-feeds/SystemLogsMarketplaceFeedsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Marketplace Feeds',
                },
            },
            {
                path: '/admin/system-logs/stock-update-logs',
                name: 'admin.system.log.stock.update.logs',
                component: () =>
                    import('@/views/admin/system-logs/stock-update-logs/SystemLogsStockUpdateLogsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Stock Update Logs',
                },
            },
        ],
    },
    {
        path: '/admin/remittances',
        name: 'admin.remittances',
        component: () => import('@/views/admin/remittances/RemittancesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Remittances',
        },
    },
    {
        path: '/admin/remittances/:id',
        name: 'admin.remittance',
        component: () => import('@/views/admin/remittances/RemittancePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Remittance',
        },
    },
    {
        path: '/admin/remittances/remittance-requests',
        name: 'admin.remittance.requests',
        component: () => import('@/views/admin/remittances/remittance-requests/RemittanceRequests.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Remittance Requests',
        },
    },
    {
        path: '/admin/forex-rates',
        name: 'admin.forex.rates',
        component: () => import('@/views/admin/forex-rates/ForexRatesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Forex Rates',
        },
    },
    {
        path: '/admin/payment-methods',
        name: 'admin.channel.account.payment.methods',
        component: () => import('@/views/admin/payment-methods/ChannelAccountPaymentMethodsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Channel Account Payment Methods',
        },
    },
    {
        path: '/admin/courier-mappings',
        name: 'admin.courier.mappings',
        component: () => import('@/views/admin/courier-mappings/CourierMappingsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Courier Mappings',
        },
    },
    {
        path: '/admin/configuration-settings',
        name: 'admin.configuration.settings',
        component: () => import('@/views/admin/configuration-settings/ConfigurationSettingsPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Configuration Settings',
        },
    },
    {
        path: '/admin/system-event',
        name: 'admin.system.event',
        redirect: '/admin/system-event/threads',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | System Event',
        },
        component: () => import('@/views/admin/system-event/SystemEventPage.vue'),
        children: [
            {
                path: '/admin/system-event/threads',
                name: 'admin.system.event.threads',
                component: () => import('@/views/admin/system-event/threads/SystemEventThreadsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | System Event Threads',
                },
            },
            {
                path: '/admin/system-event/rules',
                name: 'admin.system.event.rules',
                component: () => import('@/views/admin/system-event/rules/SystemEventRulesPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | System Event Rules',
                },
            },
            {
                path: '/admin/system-events',
                name: 'admin.system.events',
                component: () => import('@/views/admin/system-event/events/SystemEventsPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | System Events',
                },
            },
        ],
    },
    {
        path: '/admin/user-management',
        name: 'admin.users',
        redirect: '/admin/user-management/overview',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | User Management',
        },
        component: () => import('@/views/admin/users/UsersAdminPage.vue'),
        children: [
            {
                path: '/admin/user-management/overview',
                name: 'admin.users.overview',
                component: () => import('@/views/admin/users/overview/UsersOverviewPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | User Management',
                },
            },
            {
                path: '/admin/user-management/users',
                name: 'admin.users.users',
                component: () => import('@/views/admin/users/users/UsersListPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Users',
                },
            },
            {
                path: '/admin/user-management/users/:id(\\d+)',
                name: 'admin.users.user',
                component: () => import('@/views/admin/users/user/UserPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | User',
                },
            },
            {
                path: '/admin/user-management/groups',
                name: 'admin.users.groups',
                component: () => import('@/views/admin/users/groups/GroupsListPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Groups',
                },
            },
            {
                path: '/admin/user-management/groups/:id(\\d+)',
                name: 'admin.users.group',
                component: () => import('@/views/admin/users/group/GroupPage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Group',
                },
            },
            {
                path: '/admin/user-management/usage',
                name: 'admin.users.usage',
                component: () => import('@/views/admin/users/usage/UsagePage.vue'),
                meta: {
                    middleware: [authMiddleware],
                    title: 'Channel Optimiser | Usage',
                },
            },
        ],
    },
    {
        path: '/admin/user-management/:legacy(.*)*',
        redirect: legacyUserManagementTarget,
    },
    {
        path: '/admin/users/:rest(.*)*',
        redirect: formerUsersPathTarget,
    },
    {
        path: '/admin/api-keys-management',
        name: 'admin.api.keys.management',
        component: () => import('@/views/admin/api-keys-management/ApiKeysManagementPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | API Keys Management',
        },
    },
    {
        path: '/admin/api-keys-management/:id/usage',
        name: 'admin.api.keys.management.usage',
        component: () => import('@/views/admin/api-keys-management/ApiKeysManagementUsagePage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | API Key Usage',
        },
    },
    {
        path: '/import-data-sheets',
        name: 'import.data.sheets',
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Import Center',
        },
        component: () => import('@/views/import/ImportDataSheetsPage.vue'),
    },
    {
        path: '/manual-journal-entries',
        name: 'manual.journal.entries',
        component: () => import('@/views/manual-journal-entries/ManualJournalEntriesPage.vue'),
        meta: {
            middleware: [authMiddleware],
            title: 'Channel Optimiser | Manual Journal Entries',
        },
    },
]

const router = createRouter({
    history: createWebHistory('micro'),
    routes,
})

router.beforeEach((to) => {
    document.title = (to.meta?.title as string) || 'Welcome to CO'
})

router.beforeEach(carryClient)

router.afterEach((to, _from, failure) => {
    if (!failure) recordClient(to)
})

export interface MiddlewareInterface {
    // eslint-disable-next-line no-unused-vars
    (context: {
        to?: RouteLocationNormalized
        from?: RouteLocationNormalized
        next: NavigationGuardNext
        nextMiddleware: NavigationGuardNext
    }): void
}

router.beforeEach((to, from, next) => {
    if (!to.meta?.middleware) {
        return next()
    }
    const middlewares: MiddlewareInterface[] = to.meta.middleware as MiddlewareInterface[]
    const middleware = middlewares[0]
    const context = {
        to,
        from,
        next,
    }
    return middleware({
        ...context,
        nextMiddleware: middlewarePipeline(context, middlewares, 1),
    })
})

function middlewarePipeline(context: { next: NavigationGuardNext }, middleware: MiddlewareInterface[], index: number) {
    // nosemgrep: javascript.lang.security.audit.unsafe-dynamic-method.unsafe-dynamic-method
    const nextMiddleware = middleware[index]
    if (!nextMiddleware) {
        return context.next
    }
    return () => {
        const nextPipeline = middlewarePipeline(context, middleware, index + 1)
        nextMiddleware({ ...context, nextMiddleware: nextPipeline })
    }
}

export default router
